import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { Modal } from 'react-bootstrap-5';
import Button from 'shared/button';

type UnsavedDataGuardProps = {
  when: boolean;
  message: string;
  leaveText?: string;
  stayText?: string;
  title?: string;
};

// Purpose: This is a customizable version of the Prompt component from react-router-dom
function UnsavedDataGuard({
  when,
  message,
  leaveText = 'Discard Changes',
  stayText = 'Keep Editing',
  title = 'Unsaved Changes'
}: UnsavedDataGuardProps) {
  const history = useHistory();
  const [show, setShow] = useState(false);
  const nextLocationRef = useRef<any>(null);
  const unblockRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (when && !unblockRef.current) {
      unblockRef.current = history.block((tx) => {
        if (!when) return;
        nextLocationRef.current = tx;
        setShow(true);
        return false;
      });
    }

    if (!when && unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
  }, [when, history]);

  useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent) => {
      if (when) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [when]);

  const confirmLeave = () => {
    const tx = nextLocationRef.current;
    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }
    setShow(false);
    if (tx) {
      history.push(tx.pathname + (tx.search || ''));
    }
  };

  const cancelLeave = () => {
    setShow(false);
    nextLocationRef.current = null;
  };

  return (
    <Modal backdrop="static" centered show={show}  onHide={cancelLeave}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{message}</Modal.Body>
      <Modal.Footer>
        <Button.Danger outlined onClick={cancelLeave}>{stayText}</Button.Danger>
        <Button.Secondary onClick={confirmLeave}>{leaveText}</Button.Secondary>
      </Modal.Footer>
    </Modal>
  );
}

export default UnsavedDataGuard;
